import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import { rentingProcess, companyInfo } from "src/data/general";
import { backgroundImg } from "src/assets/assetsExport";
import { GuideSection, FaqAccordion } from "./GuideSection";

const viewingChecklist = [
  "Confirm the viewing time and bring photo ID — we may need this for building access and Right to Rent purposes.",
  "Note your commute: check nearest Tube, rail, and bus links at the time you would normally travel.",
  "Ask about included furnishings, appliances, and any landlord exclusions.",
  "Check mobile signal, natural light, and noise levels during the visit.",
  "Clarify permitted parking, cycle storage, and concierge or building rules.",
  "Request the EPC rating, council tax band, and approximate utility costs if not already provided.",
];

const TenantGuidesPage: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, [location]);

  return (
    <>
      <Helmet>
        <title>Tenant Guides | Letting Engine</title>
        <meta
          name="description"
          content="How to rent with Letting Engine — viewing checklist, referencing, fees transparency, moving in, and tenant FAQs for luxury London rentals."
        />
      </Helmet>

      <section className="min-h-[70vh] pt-40 pb-20 w-full relative flex items-center justify-center">
        <img
          src={backgroundImg.livingRoom2}
          alt=""
          className="absolute inset-0 w-full h-full object-cover pointer-events-none z-0"
          aria-hidden
        />
        <div className="absolute inset-0 bg-black/40 z-0" />
        <div className="relative z-10 text-center max-w-4xl px-6">
          <h1 className="font-teko leading-none mb-6 tracking-[-2px] text-6xl text-white">
            Tenant Guides
          </h1>
          <p className="font-manrope text-base leading-relaxed text-white/90 max-w-2xl mx-auto">
            Everything you need to rent with confidence — from your first viewing
            through referencing, fees, and move-in day.
          </p>
        </div>
      </section>

      <GuideSection eyebrow="The Journey" title="How to Rent with Letting Engine">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {rentingProcess.map((step, index) => (
            <div
              key={step.header}
              className="bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.06)] p-8 relative group hover:border-brand-yellow transition-colors"
            >
              <img
                src={step.img.src}
                alt={step.img.alt}
                className="h-16 mb-6 opacity-80"
              />
              <span className="font-teko text-lg text-brand-gold mb-2 block">
                0{index + 1}
              </span>
              <h3 className="font-teko text-2xl mb-3">{step.header}</h3>
              <p className="font-manrope text-sm text-text-slate leading-relaxed">
                {step.p}
              </p>
            </div>
          ))}
        </div>
      </GuideSection>

      <GuideSection
        eyebrow="Transparency"
        title="Tenant Fees"
        className="bg-bg-porcelain"
      >
        <div className="max-w-3xl space-y-6 font-manrope text-base text-text-slate leading-relaxed">
          <p>
            Letting Engine is committed to clear, upfront communication about
            costs. Fees vary by property and tenancy terms — we will always
            confirm applicable charges in writing before you commit.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-bg-white p-6 border-l-2 border-l-solid border-l-brand-yellow">
              <h3 className="font-teko text-2xl mb-3">Holding Deposit</h3>
              <p className="text-sm leading-relaxed">
                A holding deposit may be requested to reserve a property while
                referencing is completed. This is credited towards your first
                rent payment or tenancy deposit when the let proceeds, subject to
                the terms set out in your holding deposit agreement.
              </p>
            </div>
            <div className="bg-bg-white p-6 border-l-2 border-l-solid border-l-brand-yellow">
              <h3 className="font-teko text-2xl mb-3">Referencing</h3>
              <p className="text-sm leading-relaxed">
                Tenant referencing checks employment, income, and previous
                landlord references. Any referencing fees will be disclosed
                before you apply — contact us for current charges applicable to
                your tenancy.
              </p>
            </div>
            <div className="bg-bg-white p-6 border-l-2 border-l-solid border-l-brand-yellow">
              <h3 className="font-teko text-2xl mb-3">Tenancy Deposit (DPS)</h3>
              <p className="text-sm leading-relaxed">
                Your tenancy deposit is protected with the Deposit Protection
                Service (DPS), a government-approved scheme. You will receive
                prescribed information confirming how your deposit is protected
                at the start of your tenancy.
              </p>
            </div>
          </div>
          <p>
            For a full breakdown of fees, see our{" "}
            <Link
              to="/guides/tenant-fees"
              className="text-brand-gold underline hover:text-text-obsidian"
            >
              dedicated tenant fees page
            </Link>{" "}
            or{" "}
            <Link
              to="/contact-us"
              className="text-brand-gold underline hover:text-text-obsidian"
            >
              contact our team
            </Link>
            .
          </p>
        </div>
      </GuideSection>

      <GuideSection eyebrow="Preparation" title="Before Your Viewing">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
          {viewingChecklist.map((item) => (
            <li
              key={item}
              className="flex items-start gap-3 font-manrope text-sm text-text-slate leading-relaxed"
            >
              <span className="w-2 h-2 bg-brand-yellow rounded-full mt-2 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </GuideSection>

      <GuideSection
        id="right-to-rent"
        eyebrow="Compliance"
        title="Referencing & Right to Rent"
        className="bg-bg-porcelain"
      >
        <div className="max-w-3xl space-y-6 font-manrope text-base text-text-slate leading-relaxed">
          <p>
            All prospective tenants undergo referencing to confirm affordability
            and suitability. This typically includes proof of identity, proof of
            income or funds, employment verification, and previous landlord
            references where applicable.
          </p>
          <p>
            Under UK law, landlords and letting agents must check that adult
            occupants have the Right to Rent in the UK before a tenancy
            commences. You will be asked to provide original identity documents
            for verification — we will guide you through exactly what is
            required based on your circumstances.
          </p>
          <p>
            For official guidance, see the{" "}
            <a
              href="https://www.gov.uk/government/collections/landlord-guidance-on-the-right-to-rent-immigration-checks"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-gold underline hover:text-text-obsidian"
            >
              government Right to Rent guidance
            </a>
            . Questions?{" "}
            <a
              href={`mailto:${companyInfo.email}`}
              className="text-brand-gold underline hover:text-text-obsidian"
            >
              Email us
            </a>{" "}
            or call{" "}
            <a
              href={`tel:${companyInfo.phone.replace(/[^\d+]/g, "")}`}
              className="text-brand-gold underline hover:text-text-obsidian"
            >
              {companyInfo.phone}
            </a>
            .
          </p>
        </div>
      </GuideSection>

      <GuideSection eyebrow="Move-In" title="Moving In Guide">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
          {[
            {
              title: "Before move-in day",
              items: [
                "Sign your tenancy agreement and pay any agreed move-in monies.",
                "Arrange contents insurance — landlord buildings insurance does not cover your belongings.",
                "Set up council tax and utilities in your name where applicable.",
                "Confirm key collection time and inventory check-in appointment.",
              ],
            },
            {
              title: "On move-in day",
              items: [
                "Attend the check-in inventory and note any discrepancies promptly.",
                "Test heating, hot water, appliances, and smoke alarms.",
                "Photograph meter readings and keep records for your files.",
                "Save your property manager's contact details for maintenance requests.",
              ],
            },
          ].map((block) => (
            <div
              key={block.title}
              className="bg-bg-porcelain p-8 border border-solid border-[rgba(0,0,0,0.06)]"
            >
              <h3 className="font-teko text-3xl mb-6">{block.title}</h3>
              <ul className="space-y-3">
                {block.items.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 font-manrope text-sm text-text-slate"
                  >
                    <span className="w-2 h-2 bg-brand-yellow rounded-full mt-2 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </GuideSection>

      <GuideSection eyebrow="Common Questions" title="FAQ" className="bg-bg-porcelain">
        <FaqAccordion
          items={[
            {
              question: "Are properties furnished, part-furnished, or unfurnished?",
              answer:
                "It varies by property. Listings indicate the furnishing level where known. During your viewing, ask what is included — from white goods and curtains to beds and sofas — so there are no surprises on move-in day.",
            },
            {
              question: "Can I keep pets?",
              answer:
                "Pet policies depend on the landlord and building rules. Some properties welcome pets with prior approval; others do not permit them. Tell us early in your search and we will match you with suitable homes and clarify any conditions or additional deposit requirements.",
            },
            {
              question: "Are break clauses available?",
              answer:
                "Break clauses allow either party to end a fixed-term tenancy early, subject to notice periods defined in the agreement. Availability depends on the landlord's terms for each property. We will explain any break clause before you sign.",
            },
          ]}
        />
      </GuideSection>
    </>
  );
};

export default TenantGuidesPage;
